/**
 * Transient, interaction-driven visual state for the canvas scene (WI #5531).
 *
 * This is the seam between interaction and rendering: gesture handlers push state
 * here (via the renderer's bridge setters) and content painters read it. It holds
 * only ephemeral visuals that aren't already reactive in the store — the
 * long-press countdown ring and the active move's limit gradients.
 */
export class SceneState {
  /** Long-press-to-move target activity + 0–1 progress (drives the ring). */
  longPressId: string | null = null;
  longPressProgress = 0;

  /** Active horizontal move's pre/post limit positions (days) for the gradients. */
  limitPre: number | null = null;
  limitPost: number | null = null;
  /**
   * Shift-to-disable-limits is engaged: paint the gradients in the orange
   * "warning" colour instead of red (parity with MapMovementLimits' warning
   * variant). Activity + final-milestone moves set this; intermediate-milestone
   * moves never do (they only widen to the permissible positions).
   */
  limitWarning = false;

  /**
   * Banner action under the cursor (clickable state glyph / deadline), so the
   * BannerPainter can draw the SVG's blue "clickable" glow on that region.
   */
  hoverBannerActivityId: string | null = null;
  hoverBannerRegion: 'state' | 'deadline' | null = null;

  /**
   * Activity whose deadline is currently saving (the date picker's `isLoading`),
   * so the BannerPainter draws the spinner beside its deadline (SVG
   * MapActivityBanner `UiSpinner v-if="isLoadingDeadline"`). While set, the
   * renderer keeps repainting content so the spinner animates.
   */
  deadlineLoadingActivityId: string | null = null;

  /**
   * Hover targets for the hide/unhide eyes, which the SVG reveals only on hover:
   * the workstream sidebar row, the phase ribbon, and the workstream gap divider
   * (by index). Drive the per-row Eye / phase Eye / gap EyeOff visibility.
   */
  hoverWorkstreamId: string | null = null;
  hoverPhaseId: string | null = null;
  hoverGapIndex: number | null = null;

  /**
   * A member being dragged from MapGroupList and the pointer (screen px). The
   * member moves by the drag from where it is, so it is not under the pointer:
   * a tether connects the two (ListDragTetherPainter).
   */
  listDragId: string | null = null;
  listDragX = 0;
  listDragY = 0;
}
